import React, { useState, useEffect } from "react";
import { ArrowLeftIcon, AlertTriangleIcon, CheckCircle2Icon, Mail, Phone, MapPin, Building, TrendingUp, BarChart3, DollarSign, GitBranch } from "lucide-react";
import AppLayout from "../Layout/AppLayout";

const statusConfig = {
  pending: {
    label: "PENDING",
    bgColor: "bg-amber-500/10",
    borderColor: "border-amber-500/20",
    textColor: "text-amber-400",
  },
  matched: {
    label: "MATCHED",
    bgColor: "bg-blue-500/10",
    borderColor: "border-blue-500/20",
    textColor: "text-blue-400",
  },
  accepted: {
    label: "ACCEPTED",
    bgColor: "bg-[rgba(45,160,140,0.1)]",
    borderColor: "border-[rgba(45,160,140,0.3)]",
    textColor: "text-[hsl(173,58%,45%)]",
  },
  completed: {
    label: "COMPLETED",
    bgColor: "bg-green-500/10",
    borderColor: "border-green-500/20",
    textColor: "text-green-400",
  },
  declined: {
    label: "DECLINED",
    bgColor: "bg-red-500/10",
    borderColor: "border-red-500/20",
    textColor: "text-red-400",
  },
};

function formatScore(score) {
  return Math.round(score * 100);
}

function formatFee(cents) {
  return (cents / 100).toFixed(0);
}

function timeAgo(dateString) {
  if (!dateString) return null;
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now - date;
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  const diffDays = Math.floor(diffHours / 24);
  if (diffDays > 0) return `${diffDays} DAY${diffDays > 1 ? "S" : ""} AGO`;
  if (diffHours > 0) return `${diffHours} HOUR${diffHours > 1 ? "S" : ""} AGO`;
  return "JUST NOW";
}

export default function PartnerLeadDetail({
  lead,
  loading,
  onAccept,
  onDecline,
  onComplete,
  persona,
  userName,
  userInitials,
  planProgress,
  actionPlanBadge,
  isPartner,
  actionError = null,
}) {
  const [showDeclineModal, setShowDeclineModal] = useState(false);
  const [declineReason, setDeclineReason] = useState("");

  useEffect(() => {
    if (lead && lead.id && typeof window !== "undefined" && window.ahoy) {
      window.ahoy.track("succeedo_partner_lead_viewed", {
        lead_id: lead.id,
      });
    }
  }, [lead && lead.id]);

  if (loading || !lead) {
    return (
      <AppLayout
        breadcrumb="PARTNER DASHBOARD / LEAD DETAIL"
        persona={persona}
        userName={userName}
        userInitials={userInitials}
        isPartner={isPartner}
        planProgress={planProgress}
        actionPlanBadge={actionPlanBadge}
      >
        <div className="p-6">
          <div className="animate-pulse space-y-6">
            <div className="h-4 w-32 bg-muted rounded" />
            <div className="h-40 bg-muted rounded-[16px]" />
            <div className="h-60 bg-muted rounded-[16px]" />
          </div>
        </div>
      </AppLayout>
    );
  }

  const currentStatus = lead.status;
  const config = statusConfig[currentStatus] || statusConfig.pending;

  const assessmentScores = lead.user_context?.assessment_scores || {};
  const redFlagCategories = lead.user_context?.red_flags || [];

  const scoreEntries = Object.entries(assessmentScores).map(
    ([category, score]) => ({
      category,
      score: formatScore(score),
      isRedFlag: redFlagCategories.includes(category),
    })
  );

  const redFlags = scoreEntries.filter((s) => s.isRedFlag);

  const handleAccept = () => {
    if (onAccept) onAccept();
  };

  const handleDecline = () => {
    setShowDeclineModal(true);
  };

  const confirmDecline = () => {
    if (onDecline) onDecline(declineReason);
    setShowDeclineModal(false);
  };

  return (
    <AppLayout
      breadcrumb="PARTNER DASHBOARD / LEAD DETAIL"
      persona={persona}
      userName={userName}
      userInitials={userInitials}
      isPartner={isPartner}
      planProgress={planProgress}
      actionPlanBadge={actionPlanBadge}
    >
      <div className="p-6">
        {/* Back Link */}
        <a
          href="/succeedo/partner/dashboard"
          className="inline-flex items-center gap-2 text-[13px] text-muted-foreground hover:text-foreground transition-colors mb-6"
        >
          <ArrowLeftIcon className="w-4 h-4" />
          Back to leads
        </a>

        {/* Owner Context Card */}
        <div className="bg-card border border-border rounded-[16px] p-6 mb-6">
          <div className="flex items-start justify-between mb-4">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 rounded-full bg-muted text-foreground flex items-center justify-center font-mono text-[14px] font-medium">
                {lead.owner_initials}
              </div>
              <div>
                <div className="font-serif text-[24px] font-light text-foreground mb-1">
                  {lead.owner_name}
                </div>
                <div className="text-[14px] text-muted-foreground">
                  {lead.industry} · {lead.province}
                </div>
              </div>
            </div>
            <div
              className={`rounded-full px-3 py-1 font-mono text-[10px] uppercase tracking-wide border ${config.bgColor} ${config.borderColor} ${config.textColor}`}
            >
              {config.label}
            </div>
          </div>

          {/* Key Metrics Row */}
          <div className="flex items-center gap-8 pt-4 border-t border-border">
            <div>
              <div className="font-mono text-[10px] uppercase text-muted-foreground/50 tracking-wide mb-1">
                ASSESSMENT TIER
              </div>
              <div className="text-[16px] font-medium text-foreground">
                {lead.user_context?.assessment_tier || "N/A"}
              </div>
            </div>
            <div>
              <div className="font-mono text-[10px] uppercase text-muted-foreground/50 tracking-wide mb-1">
                PARTNER CATEGORY
              </div>
              <div className="text-[16px] font-medium text-foreground">
                {lead.partner_category || "N/A"}
              </div>
            </div>
            <div>
              <div className="font-mono text-[10px] uppercase text-muted-foreground/50 tracking-wide mb-1">
                TOOL EVENTS
              </div>
              <div className="text-[16px] font-medium text-foreground">
                {lead.user_context?.tool_events?.length || 0}
              </div>
            </div>
          </div>
        </div>

        {/* The owner's brief (George P4-4): written and approved by the owner,
            shown only while they keep sharing turned on. */}
        {lead.brief && (
          <div className="bg-card border border-border rounded-[16px] p-6 mb-6" data-testid="lead-brief">
            <div className="font-mono text-[10px] uppercase text-muted-foreground/50 tracking-wide mb-3">
              FROM THE OWNER
            </div>
            {lead.brief.split('\n').map((line) => (
              <p key={line} className="m-0 mb-1.5 text-[14px] leading-[1.55] text-foreground">{line}</p>
            ))}
            <p className="m-0 mt-3 text-[12px] text-muted-foreground">
              Shared with the owner&apos;s consent for this introduction only. Please don&apos;t pass it on.
            </p>
          </div>
        )}

        {/* Contact & Business Info */}
        <div className="bg-card border border-border rounded-[16px] p-6 mb-6">
          <div className="font-mono text-[10px] uppercase text-muted-foreground/50 tracking-wide mb-4">
            CONTACT INFORMATION
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {lead.owner_email && (
              <div className="flex items-center gap-3">
                <Mail className="w-4 h-4 text-muted-foreground/40" />
                <a href={`mailto:${lead.owner_email}`} className="text-[14px] text-muted-foreground hover:text-foreground transition-colors">
                  {lead.owner_email}
                </a>
              </div>
            )}
            {lead.owner_phone && (
              <div className="flex items-center gap-3">
                <Phone className="w-4 h-4 text-muted-foreground/40" />
                <span className="text-[14px] text-muted-foreground">{lead.owner_phone}</span>
              </div>
            )}
            {(lead.owner_city || lead.owner_province) && (
              <div className="flex items-center gap-3">
                <MapPin className="w-4 h-4 text-muted-foreground/40" />
                <span className="text-[14px] text-muted-foreground">
                  {[lead.owner_city, lead.owner_province].filter(Boolean).join(', ')}
                </span>
              </div>
            )}
            {lead.owner_company_name && (
              <div className="flex items-center gap-3">
                <Building className="w-4 h-4 text-muted-foreground/40" />
                <span className="text-[14px] text-muted-foreground">{lead.owner_company_name}</span>
              </div>
            )}
          </div>
          {(lead.owner_persona || lead.owner_goal) && (
            <div className="mt-4 pt-4 border-t border-border">
              <div className="text-[13px] text-muted-foreground">
                {lead.owner_persona && <span className="capitalize">{lead.owner_persona}</span>}
                {lead.owner_goal && <span> — {lead.owner_goal}</span>}
              </div>
            </div>
          )}
        </div>

        {/* Assessment Scores Section */}
        {scoreEntries.length > 0 && (
          <div className="mb-6">
            <div className="flex items-center gap-2 mb-4">
              <div className="w-2 h-2 rounded-full bg-[hsl(var(--succeedo-orange))] shadow-[0_0_8px_hsl(var(--succeedo-orange))]" />
              <span className="font-mono text-[11px] uppercase tracking-wider text-muted-foreground/40">
                ASSESSMENT SCORES
              </span>
            </div>

            <div className="bg-card border border-border rounded-[16px] p-6 space-y-4">
              {scoreEntries.map((score) => (
                <div key={score.category}>
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <span className="text-[13px] text-foreground">
                        {score.category}
                      </span>
                      {score.isRedFlag && (
                        <AlertTriangleIcon className="w-3 h-3 text-red-400" />
                      )}
                    </div>
                    <span className="text-[13px] text-foreground">
                      {score.score}%
                    </span>
                  </div>
                  <div className="h-2 bg-border rounded-full overflow-hidden">
                    <div
                      className={`h-full ${score.isRedFlag ? "bg-red-400" : "bg-[hsl(173,58%,45%)]"}`}
                      style={{ width: `${score.score}%` }}
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Red Flags Callout */}
        {redFlags.length > 0 && (
          <div className="bg-red-500/5 border border-red-500/15 rounded-[12px] p-4 mb-6">
            <div className="flex items-start gap-3">
              <AlertTriangleIcon className="w-4 h-4 text-red-400 shrink-0 mt-0.5" />
              <div className="flex-1">
                <div className="font-mono text-[11px] uppercase text-red-400 tracking-wide mb-2">
                  RED FLAGS IDENTIFIED
                </div>
                <ul className="space-y-1">
                  {redFlags.map((flag) => (
                    <li
                      key={flag.category}
                      className="text-[13px] text-foreground"
                    >
                      {flag.category}: {flag.score}%
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        )}

        {/* Valuation History */}
        {lead.valuation_snapshots && lead.valuation_snapshots.length > 0 && (
          <div className="mb-6">
            <div className="flex items-center gap-2 mb-4">
              <div className="w-2 h-2 rounded-full bg-[hsl(var(--succeedo-orange))] shadow-[0_0_8px_hsl(var(--succeedo-orange))]" />
              <span className="font-mono text-[11px] uppercase tracking-wider text-muted-foreground/40">
                VALUATION HISTORY ({lead.valuation_snapshots.length})
              </span>
            </div>

            <div className="space-y-3">
              {lead.valuation_snapshots.map((snap, idx) => (
                <div key={snap.id} className="bg-card border border-border rounded-[16px] p-5">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-3">
                      <BarChart3 className="w-5 h-5 text-[hsl(var(--succeedo-orange))]" />
                      <div>
                        <div className="text-[14px] font-medium text-foreground">
                          {new Date(snap.created_at).toLocaleDateString('en-CA', { year: 'numeric', month: 'short', day: 'numeric' })}
                        </div>
                        <div className="text-[12px] text-muted-foreground">
                          {snap.source === 'quickbooks' ? '🔗 QuickBooks-Verified' : 'Self-Reported'}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {idx === 0 && (
                        <span className="font-mono text-[10px] uppercase bg-[hsl(var(--succeedo-orange))]/10 text-[hsl(var(--succeedo-orange))] border border-[hsl(var(--succeedo-orange))]/20 rounded-full px-2 py-0.5 tracking-wide">
                          LATEST
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Range — ValuationSnapshot.value_range is stored in
                      dollars (same source the seller's /succeedo/valuation
                      reads), not cents. Cast through Number() because the
                      JSON resolver hands floats back as strings, which
                      bypass toLocaleString's fraction-digit formatting and
                      leak IEEE noise like 24575199.999999999. */}
                  <div className="text-[20px] font-light text-foreground mb-3">
                    ${Number(snap.range_low || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} — ${Number(snap.range_high || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </div>

                  {/* Key Inputs Grid */}
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 pt-3 border-t border-border">
                    {snap.revenue && (
                      <div>
                        <div className="font-mono text-[10px] uppercase text-muted-foreground/40 tracking-wide mb-1">REVENUE</div>
                        <div className="text-[13px] text-foreground">${(snap.revenue || 0).toLocaleString()}</div>
                      </div>
                    )}
                    {snap.ebitda != null && (
                      <div>
                        <div className="font-mono text-[10px] uppercase text-muted-foreground/40 tracking-wide mb-1">EBITDA</div>
                        <div className="text-[13px] text-foreground">${(snap.ebitda || 0).toLocaleString()}</div>
                      </div>
                    )}
                    {snap.recurring_revenue_pct != null && (
                      <div>
                        <div className="font-mono text-[10px] uppercase text-muted-foreground/40 tracking-wide mb-1">RECURRING</div>
                        <div className="text-[13px] text-foreground">{((snap.recurring_revenue_pct || 0) * 100).toFixed(0)}%</div>
                      </div>
                    )}
                    {snap.growth_rate != null && (
                      <div>
                        <div className="font-mono text-[10px] uppercase text-muted-foreground/40 tracking-wide mb-1">GROWTH</div>
                        <div className="text-[13px] text-foreground">{((snap.growth_rate || 0) * 100).toFixed(1)}%</div>
                      </div>
                    )}
                  </div>

                  {/* Industry + Adjustments */}
                  <div className="flex flex-wrap gap-3 mt-3 pt-3 border-t border-border">
                    {snap.sector_cluster && (
                      <span className="font-mono text-[11px] uppercase rounded-full border border-border text-muted-foreground px-3 py-1">
                        {snap.sector_cluster}
                      </span>
                    )}
                    {snap.industry_naics_code && (
                      <span className="font-mono text-[11px] rounded-full border border-border text-muted-foreground px-3 py-1">
                        NAICS {snap.industry_naics_code}
                      </span>
                    )}
                    {snap.adjustments && Object.keys(snap.adjustments).length > 0 && (
                      <span className="font-mono text-[11px] uppercase rounded-full border border-[hsl(var(--succeedo-orange))]/20 text-[hsl(var(--succeedo-orange))] px-3 py-1">
                        {Object.keys(snap.adjustments).length} ADJUSTMENTS
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Financing History */}
        {lead.financing_snapshots && lead.financing_snapshots.length > 0 && (
          <div className="mb-6">
            <div className="flex items-center gap-2 mb-4">
              <div className="w-2 h-2 rounded-full bg-[hsl(var(--succeedo-orange))] shadow-[0_0_8px_hsl(var(--succeedo-orange))]" />
              <span className="font-mono text-[11px] uppercase tracking-wider text-muted-foreground/40">
                FINANCING HISTORY ({lead.financing_snapshots.length})
              </span>
            </div>
            <div className="space-y-3">
              {lead.financing_snapshots.map((snap, idx) => (
                <div key={snap.id} className="bg-card border border-border rounded-[16px] p-5">
                  {/* Header: date + LATEST badge */}
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-3">
                      <DollarSign className="w-5 h-5 text-[hsl(var(--succeedo-orange))]" />
                      <div>
                        <div className="text-[14px] font-medium text-foreground">
                          {new Date(snap.created_at).toLocaleDateString('en-CA', { year: 'numeric', month: 'short', day: 'numeric' })}
                        </div>
                        {snap.lender_name && (
                          <div className="text-[12px] text-muted-foreground">{snap.lender_name}</div>
                        )}
                      </div>
                    </div>
                    {idx === 0 && (
                      <span className="font-mono text-[10px] uppercase bg-[hsl(var(--succeedo-orange))]/10 text-[hsl(var(--succeedo-orange))] border border-[hsl(var(--succeedo-orange))]/20 rounded-full px-2 py-0.5">
                        LATEST
                      </span>
                    )}
                  </div>

                  {/* Monthly Payment hero */}
                  <div className="text-[20px] font-light text-foreground mb-3">
                    ${(snap.results?.monthly_payment || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}/mo
                  </div>

                  {/* Key Inputs Grid */}
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 pt-3 border-t border-border">
                    {snap.results?.loan_amount && (
                      <div>
                        <div className="font-mono text-[10px] uppercase text-muted-foreground/40 tracking-wide mb-1">LOAN AMOUNT</div>
                        <div className="text-[13px] text-foreground">${(snap.results.loan_amount || 0).toLocaleString()}</div>
                      </div>
                    )}
                    {snap.inputs?.purchase_price && (
                      <div>
                        <div className="font-mono text-[10px] uppercase text-muted-foreground/40 tracking-wide mb-1">PURCHASE PRICE</div>
                        <div className="text-[13px] text-foreground">${(snap.inputs.purchase_price || 0).toLocaleString()}</div>
                      </div>
                    )}
                    {snap.inputs?.interest_rate != null && (
                      <div>
                        <div className="font-mono text-[10px] uppercase text-muted-foreground/40 tracking-wide mb-1">RATE</div>
                        <div className="text-[13px] text-foreground">{((snap.inputs.interest_rate || 0) * 100).toFixed(1)}%</div>
                      </div>
                    )}
                    {snap.inputs?.loan_term_years && (
                      <div>
                        <div className="font-mono text-[10px] uppercase text-muted-foreground/40 tracking-wide mb-1">TERM</div>
                        <div className="text-[13px] text-foreground">{snap.inputs.loan_term_years} years</div>
                      </div>
                    )}
                  </div>

                  {/* Lender badge */}
                  {snap.lender_name && (
                    <div className="flex flex-wrap gap-3 mt-3 pt-3 border-t border-border">
                      <span className="font-mono text-[11px] uppercase rounded-full border border-border text-muted-foreground px-3 py-1">
                        {snap.lender_name}
                      </span>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Deal Structure History */}
        {lead.deal_structure_snapshots && lead.deal_structure_snapshots.length > 0 && (
          <div className="mb-6">
            <div className="flex items-center gap-2 mb-4">
              <div className="w-2 h-2 rounded-full bg-[hsl(var(--succeedo-orange))] shadow-[0_0_8px_hsl(var(--succeedo-orange))]" />
              <span className="font-mono text-[11px] uppercase tracking-wider text-muted-foreground/40">
                DEAL STRUCTURE HISTORY ({lead.deal_structure_snapshots.length})
              </span>
            </div>
            <div className="space-y-3">
              {lead.deal_structure_snapshots.map((snap, idx) => (
                <div key={snap.id} className="bg-card border border-border rounded-[16px] p-5">
                  {/* Header: date + structure type badge + LATEST */}
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-3">
                      <GitBranch className="w-5 h-5 text-[hsl(var(--succeedo-orange))]" />
                      <div>
                        <div className="text-[14px] font-medium text-foreground">
                          {new Date(snap.created_at).toLocaleDateString('en-CA', { year: 'numeric', month: 'short', day: 'numeric' })}
                        </div>
                        {snap.structure_type && (
                          <div className="text-[12px] text-muted-foreground capitalize">
                            {snap.structure_type.replace(/_/g, ' ')}
                          </div>
                        )}
                      </div>
                    </div>
                    {idx === 0 && (
                      <span className="font-mono text-[10px] uppercase bg-[hsl(var(--succeedo-orange))]/10 text-[hsl(var(--succeedo-orange))] border border-[hsl(var(--succeedo-orange))]/20 rounded-full px-2 py-0.5">
                        LATEST
                      </span>
                    )}
                  </div>

                  {/* After-tax proceeds hero */}
                  <div className="text-[20px] font-light text-foreground mb-3">
                    ${(snap.after_tax_proceeds || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}
                    <span className="text-[13px] text-muted-foreground ml-2">after tax</span>
                  </div>

                  {/* Key Inputs Grid */}
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 pt-3 border-t border-border">
                    {snap.inputs?.asking_price != null && (
                      <div>
                        <div className="font-mono text-[10px] uppercase text-muted-foreground/40 tracking-wide mb-1">ASKING PRICE</div>
                        <div className="text-[13px] text-foreground">${(snap.inputs.asking_price || 0).toLocaleString()}</div>
                      </div>
                    )}
                    {snap.cash_at_closing != null && (
                      <div>
                        <div className="font-mono text-[10px] uppercase text-muted-foreground/40 tracking-wide mb-1">CASH AT CLOSING</div>
                        <div className="text-[13px] text-foreground">${(snap.cash_at_closing || 0).toLocaleString()}</div>
                      </div>
                    )}
                    {snap.inputs?.province && (
                      <div>
                        <div className="font-mono text-[10px] uppercase text-muted-foreground/40 tracking-wide mb-1">PROVINCE</div>
                        <div className="text-[13px] text-foreground">{snap.inputs.province}</div>
                      </div>
                    )}
                    {snap.results?.effective_tax_rate != null && (
                      <div>
                        <div className="font-mono text-[10px] uppercase text-muted-foreground/40 tracking-wide mb-1">EFFECTIVE TAX RATE</div>
                        <div className="text-[13px] text-foreground">{((snap.results.effective_tax_rate || 0) * 100).toFixed(1)}%</div>
                      </div>
                    )}
                  </div>

                  {/* Structure type badge */}
                  {snap.structure_type && (
                    <div className="flex flex-wrap gap-3 mt-3 pt-3 border-t border-border">
                      <span className="font-mono text-[11px] uppercase rounded-full border border-border text-muted-foreground px-3 py-1">
                        {snap.structure_type.replace(/_/g, ' ')}
                      </span>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Tool Activity */}
        {lead.tool_events && lead.tool_events.length > 0 && (
          <div className="mb-6">
            <div className="flex items-center gap-2 mb-4">
              <div className="w-2 h-2 rounded-full bg-[hsl(var(--succeedo-orange))] shadow-[0_0_8px_hsl(var(--succeedo-orange))]" />
              <span className="font-mono text-[11px] uppercase tracking-wider text-muted-foreground/40">
                TOOL ACTIVITY
              </span>
            </div>
            <div className="bg-card border border-border rounded-[16px] p-5 space-y-3">
              {lead.tool_events.map((event, idx) => (
                <div key={idx} className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <TrendingUp className="w-4 h-4 text-muted-foreground/40" />
                    <span className="text-[13px] text-foreground capitalize">
                      {(event.event_type || '').replace(/_/g, ' ')}
                    </span>
                  </div>
                  {event.completed_at && (
                    <span className="text-[12px] text-muted-foreground">
                      {new Date(event.completed_at).toLocaleDateString()}
                    </span>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Introduction Fee */}
        <div className="bg-[hsl(var(--succeedo-orange))]/5 border border-[hsl(var(--succeedo-orange))]/20 rounded-[12px] p-4 mb-6">
          <div className="font-mono text-[11px] uppercase text-muted-foreground/50 tracking-wide mb-2">
            INTRODUCTION FEE
          </div>
          <div className="text-[24px] font-light text-foreground mb-1">
            ${lead.per_lead_fee_cents ? formatFee(lead.per_lead_fee_cents) : "0"}
          </div>
          <div className="text-[13px] text-muted-foreground">
            Charged on acceptance
          </div>
        </div>

        {actionError && (
          <p role="alert" className="m-0 mb-3 text-[13px] text-coral-ink">{actionError}</p>
        )}

        {/* Action Buttons */}
        {(currentStatus === "pending" || currentStatus === "matched") && (
          <div className="flex items-center gap-3">
            <button
              onClick={handleAccept}
              className="flex-1 bg-[hsl(var(--succeedo-orange))] text-white rounded-[8px] px-8 py-3 text-[14px] font-medium hover:bg-[#e65d0a] transition-colors"
            >
              Accept Introduction
            </button>
            <button
              onClick={handleDecline}
              className="flex-1 bg-transparent border border-border text-muted-foreground rounded-[8px] px-8 py-3 text-[14px] hover:bg-muted transition-colors"
            >
              Decline
            </button>
          </div>
        )}

        {currentStatus === "accepted" && (
          <div className="space-y-3">
            <a
              href={`/succeedo/partner/messages?lead=${lead.id}`}
              className="block w-full bg-[hsl(173,58%,45%)] text-white rounded-[8px] px-8 py-3 text-[14px] font-medium hover:opacity-90 transition-opacity text-center"
            >
              Message Owner
            </a>
            <button
              type="button"
              onClick={() => { if (onComplete) onComplete(); }}
              className="w-full border border-border text-foreground rounded-[8px] px-8 py-3 text-[14px] font-medium hover:bg-accent transition-colors"
            >
              <CheckCircle2Icon className="w-4 h-4 inline mr-2" />
              Mark Engagement Complete
            </button>

            {/* Status Timeline */}
            <div className="bg-card border border-border rounded-[16px] p-6">
              <div className="font-mono text-[11px] uppercase text-muted-foreground/40 tracking-wide mb-4">
                STATUS TIMELINE
              </div>
              <div className="space-y-4">
                {lead.accepted_at && (
                  <div className="flex gap-3">
                    <div className="flex flex-col items-center">
                      <div className="w-2 h-2 rounded-full bg-[hsl(173,58%,45%)]" />
                      <div className="w-0.5 h-full bg-border" />
                    </div>
                    <div className="flex-1 pb-4">
                      <div className="font-mono text-[10px] text-muted-foreground mb-1">
                        {timeAgo(lead.accepted_at)}
                      </div>
                      <div className="text-[13px] text-foreground">
                        Introduction accepted
                      </div>
                    </div>
                  </div>
                )}
                {lead.matched_at && (
                  <div className="flex gap-3">
                    <div className="flex flex-col items-center">
                      <div className="w-2 h-2 rounded-full bg-border" />
                      <div className="w-0.5 h-full bg-border" />
                    </div>
                    <div className="flex-1 pb-4">
                      <div className="font-mono text-[10px] text-muted-foreground mb-1">
                        {timeAgo(lead.matched_at)}
                      </div>
                      <div className="text-[13px] text-foreground">
                        Lead matched to your profile
                      </div>
                    </div>
                  </div>
                )}
                <div className="flex gap-3">
                  <div className="w-2 h-2 rounded-full bg-border" />
                  <div className="flex-1">
                    <div className="font-mono text-[10px] text-muted-foreground mb-1">
                      {timeAgo(lead.created_at)}
                    </div>
                    <div className="text-[13px] text-foreground">
                      Owner requested introduction
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Decline Confirmation Modal */}
        {showDeclineModal && (
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <div className="bg-white dark:bg-card rounded-[16px] p-6 max-w-md w-full">
              <h3 className="text-[18px] font-semibold text-foreground mb-2">
                Decline Introduction
              </h3>
              <p className="text-[14px] text-muted-foreground mb-4">
                Are you sure you want to decline this introduction? This action
                cannot be undone.
              </p>
              <div className="mb-4">
                <label className="block text-[13px] text-muted-foreground mb-2">
                  Reason (optional)
                </label>
                <textarea
                  value={declineReason}
                  onChange={(e) => setDeclineReason(e.target.value)}
                  placeholder="Let us know why you're declining..."
                  className="w-full bg-background border border-border rounded-[8px] px-3 py-2 text-[14px] text-foreground focus:outline-none focus:ring-2 focus:ring-[hsl(var(--succeedo-orange))]/20 min-h-[100px]"
                />
              </div>
              <div className="flex items-center gap-3">
                <button
                  onClick={confirmDecline}
                  className="flex-1 bg-red-500 text-white rounded-[8px] px-6 py-2.5 text-[14px] font-medium hover:bg-red-600 transition-colors"
                >
                  Confirm Decline
                </button>
                <button
                  onClick={() => setShowDeclineModal(false)}
                  className="flex-1 bg-transparent border border-border text-foreground rounded-[8px] px-6 py-2.5 text-[14px] hover:bg-muted transition-colors"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </AppLayout>
  );
}
